import styles from './style.module.scss';
import { Table, Row, Col, Popconfirm, Tag } from 'antd';
import { Button, PageHeader } from 'components';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import useShoppingCenter from '../useShoppingCenter';
import {
  DeleteFilled,
  EditOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const RIMAIL_API_URL = process.env.REACT_APP_RIMAIL_API_URL;

const SingleShop = () => {
  const { singleShop } = useShoppingCenter(false);
  const [rimailLoading, setRimailLoading] = useState(false);
  const [rimailSources, setRimailSources] = useState([]);
  const [agentStatus, setAgentStatus] = useState('');
  const [agentSyncStatus, setAgentSyncStatus] = useState('');
  const [agentTotalCost, setAgentTotalCost] = useState('');
  const [agentTotalMessages, setAgentTotalMessages] = useState(0);
  const [agentTotalTokensUsed, setAgentTotalTokensUsed] = useState(0);
  const [deletingSourceId, setDeletingSourceId] = useState(null);
  const [runningSourceId, setRunningSourceId] = useState(null);

  useEffect(() => {
    const fetchRimailAgent = async () => {
      if (!singleShop?.rimail_agent_id || !singleShop?.rimail_token) {
        setRimailSources([]);
        setAgentStatus('');
        setAgentSyncStatus('');
        setAgentTotalCost('');
        setAgentTotalMessages(0);
        setAgentTotalTokensUsed(0);
        return;
      }

      setRimailLoading(true);
      try {
        const response = await axios.get(
          `${RIMAIL_API_URL}/chatgpt_agents/${singleShop.rimail_agent_id}`,
          {
            headers: {
              'auth-token': singleShop.rimail_token,
            },
          },
        );

        const rimailAgent = response?.data?.chatgpt_agent || {};
        const sources = rimailAgent?.chatgpt_agent_sources || [];
        setAgentStatus(rimailAgent?.status || '');
        setAgentSyncStatus(rimailAgent?.sync_status || '');
        setAgentTotalCost(rimailAgent?.total_cost || '');
        setAgentTotalMessages(rimailAgent?.total_messages || 0);
        setAgentTotalTokensUsed(rimailAgent?.total_tokens_used || 0);
        setRimailSources(Array.isArray(sources) ? sources : []);
      } catch (e) {
        setRimailSources([]);
        setAgentStatus('');
        setAgentSyncStatus('');
        setAgentTotalCost('');
        setAgentTotalMessages(0);
        setAgentTotalTokensUsed(0);
      } finally {
        setRimailLoading(false);
      }
    };

    fetchRimailAgent();
  }, [singleShop?.rimail_agent_id, singleShop?.rimail_token]);

  const rows = useMemo(() => {
    return rimailSources.map((source) => ({
      key: source.id,
      source_name: source.source_name || '-',
      source_url: source.source_url || '-',
      pages_count: source?.chatgpt_agent_pages?.length || 0,
    }));
  }, [rimailSources]);

  const handleDeleteSource = async (sourceId) => {
    if (!singleShop?.rimail_agent_id || !singleShop?.rimail_token || !sourceId)
      return;
    setDeletingSourceId(sourceId);
    try {
      await axios.delete(
        `${RIMAIL_API_URL}/chatgpt_agents/${singleShop.rimail_agent_id}/chatgpt_agent_sources/${sourceId}`,
        {
          headers: {
            'auth-token': singleShop.rimail_token,
          },
        },
      );
      setRimailSources((prev) => prev.filter((item) => item.id !== sourceId));
    } finally {
      setDeletingSourceId(null);
    }
  };

  const handleRunSource = async (sourceId) => {
    if (!singleShop?.rimail_agent_id || !singleShop?.rimail_token || !sourceId)
      return;
    if (agentSyncStatus === 'syncing') return;
    setRunningSourceId(sourceId);
    try {
      await axios.post(
        `${RIMAIL_API_URL}/chatgpt_agents/${singleShop.rimail_agent_id}/chatgpt_agent_sources/${sourceId}/crawl`,
        {},
        {
          headers: {
            'auth-token': singleShop.rimail_token,
          },
        },
      );
    } finally {
      setRunningSourceId(null);
    }
  };

  const columns = [
    {
      title: 'Nome sorgente',
      dataIndex: 'source_name',
      key: 'source_name',
      width: 300,
    },
    {
      title: 'URL sorgente',
      dataIndex: 'source_url',
      key: 'source_url',
    },
    {
      title: 'Pagine',
      dataIndex: 'pages_count',
      key: 'pages_count',
      width: 100,
      align: 'right',
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      align: 'right',
      render: (_, row) => (
        <Row gutter={8} justify={'end'}>
          <Col>
            <Link to={`/admin/agents/${singleShop.id}/sources/${row.key}/edit`}>
              <Button type={'secondary'} icon={<EditOutlined />} />
            </Link>
          </Col>
          <Col>
            <Popconfirm
              title={'Eliminare la sorgente?'}
              description={'Questa azione non può essere annullata.'}
              okText={'Elimina'}
              cancelText={'Annulla'}
              onConfirm={() => handleDeleteSource(row.key)}
            >
              <Button
                type={'red'}
                icon={<DeleteFilled />}
                loading={deletingSourceId === row.key}
              />
            </Popconfirm>
          </Col>
          <Col>
            <Link to={`/admin/agents/${singleShop.id}/sources/${row.key}`}>
              <Button type={'secondary'} icon={<EyeOutlined />} />
            </Link>
          </Col>
          <Col>
            <Button
              type={'green'}
              icon={<PlayCircleOutlined />}
              loading={runningSourceId === row.key}
              disabled={agentSyncStatus === 'syncing'}
              onClick={() => handleRunSource(row.key)}
            />
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={'Sorgente agente'} />
      <div className={styles.content}>
        <Row gutter={16}>
          <Col>
            <Link to={`/admin/agents/${singleShop.id}/sources/new`}>
              <Button icon={<PlusOutlined />}>Nuova sorgente</Button>
            </Link>
          </Col>
        </Row>
        <Row gutter={12} className={styles.statsRow}>
          <Col>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Costo totale</div>
              <div className={styles.statValue}>
                {agentTotalCost !== '' && agentTotalCost != null
                  ? agentTotalCost
                  : '-'}
              </div>
            </div>
          </Col>
          <Col>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Messaggi totali</div>
              <div className={styles.statValue}>{agentTotalMessages}</div>
            </div>
          </Col>
          <Col>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Token utilizzati</div>
              <div className={styles.statValue}>{agentTotalTokensUsed}</div>
            </div>
          </Col>
        </Row>
        <Table
          title={() => (
            <Row gutter={12}>
              <Col>
                <span>Stato: </span>
                <Tag color={agentStatus === 'active' ? 'green' : 'default'}>
                  {agentStatus || '-'}
                </Tag>
              </Col>
              <Col>
                <span>Stato sincronizzazione: </span>
                <Tag
                  color={
                    agentSyncStatus === 'syncing' ? 'processing' : 'default'
                  }
                >
                  {agentSyncStatus || '-'}
                </Tag>
              </Col>
            </Row>
          )}
          className={styles.table}
          columns={columns}
          dataSource={rows}
          rowKey='key'
          loading={rimailLoading}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default SingleShop;
